/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Spin,
  Row,
  Col,
  Card,
  Input,
  DatePicker,
} from 'antd';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import { checkToken } from 'api/utils';
import { formatReceived } from 'utils/formatValues';
import FormSubmitControls from 'components/FormSubmitControls';
import { onError } from 'utils/handlers';
import {
  deleteSuspensionDePagoDocumento,
  getSuspensionDePagoDocumento,
  getSuspensionesDePagosDocumentos,
  patchSuspensionDePagoDocumento,
  postSuspensionDePagoDocumento,
} from 'api/nominas/suspensiones/suspensionesDePagosDocumentos';
import TextArea from 'antd/lib/input/TextArea';

export const suspensionDatosPermission = {
  permissionModel: 'suspensiondepagoempleadodocumento',
  catalogsModels: [],
  tabsModels: [],
};

const SuspensionDePagosDatosInformativos = ({
  suspensionId,
  permission,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getSuspensionesDePagosDocumentos();
    setData(formatReceived(_data?.filter((e) => e.suspension_de_pago_empleado === suspensionId)));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _data,
      ] = await Promise.all([
        getSuspensionesDePagosDocumentos({ suspension_de_pago_empleado: suspensionId }),
      ]);
      if (mounted) {
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, [suspensionId]);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _suspensionDocumento = await getSuspensionDePagoDocumento(match.id);
    setTimeout(() => {
      if (_suspensionDocumento) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_suspensionDocumento));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchSuspensionDePagoDocumento(key, { ...values, suspension_de_pago_empleado: suspensionId }) : postSuspensionDePagoDocumento({ ...values, suspension_de_pago_empleado: suspensionId }));
      if (saved) {
        if (_continue) {
          await fetchData();
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteSuspensionDePagoDocumento(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'No. de Expediente',
      dataIndex: 'no_de_expediente',
      key: 'no_de_expediente',
      width: 200,
    },
    {
      titleText: 'Fecha de Oficio',
      dataIndex: 'fecha_de_oficio',
      key: 'fecha_de_oficio',
      width: 200,
      render: (val) => val?.format(),
    },
    {
      titleText: 'Juzgado',
      dataIndex: 'juzgado',
      key: 'juzgado',
      width: 300,
    },
    {
      titleText: 'Nombre del Juez',
      dataIndex: 'nombre_del_juez',
      key: 'nombre_del_juez',
      width: 350,
    },
    {
      titleText: 'Asunto',
      dataIndex: 'asunto',
      key: 'asunto',
      width: 300,
    },
    {
      titleText: 'Estado del Asunto',
      dataIndex: 'estado_del_asunto',
      key: 'estado_del_asunto',
      width: 300,
    },
    {
      titleText: 'Resolución',
      dataIndex: 'resolucion',
      key: 'resolucion',
      width: 300,
    },
    {
      titleText: 'Acciones Adicionales',
      dataIndex: 'acciones_adicionales',
      key: 'acciones_adicionales',
      width: 400,
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 400,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!open ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit: () => onClickEdit(),
              onClickDelete,
            }}
            permission={permission}
            allowExportPDF={false}
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Dato Informativo`}
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
                loading={loading}
              />
            )}
            bordered={false}
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              scrollToFirstError
              name="general"
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="no_de_expediente"
                    label="No. de Expediente"
                    rules={rules.required}
                    hasFeedback
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="fecha_de_oficio"
                    label="Fecha de Oficio"
                  >
                    <DatePicker
                      allowClear
                      format="DD/MM/YYYY"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="juzgado"
                    label="Juzgado"
                  >
                    <Input
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="nombre_del_juez"
                    label="Nombre del Juez"
                  >
                    <Input
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="asunto"
                    label="Asunto"
                  >
                    <Input
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="estado_del_asunto"
                    label="Estado del Asunto"
                  >
                    <Input
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="resolucion"
                    label="Resolución"
                  >
                    <Input
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={16}>
                  <Form.Item
                    name="acciones_adicionales"
                    label="Acciones Adicionales"
                  >
                    <Input
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={24}>
                  <Form.Item
                    name="observaciones"
                    label="Observaciones"
                  >
                    <TextArea
                      rows={3}
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="Dato Informativo"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default SuspensionDePagosDatosInformativos;
